import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from '../../components/toast/CustomToast'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Modal from '../../components/common/Modal'
import api from '../../utils/api'

const AdminUsersPage = () => {
    const { token } = useAuth()
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
    const [confirmAction, setConfirmAction] = useState(null)
    const [selectedUser, setSelectedUser] = useState(null)
    const [editingUser, setEditingUser] = useState(null)
    const [formData, setFormData] = useState({ username: '', email: '' })
    const [formErrors, setFormErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')

    // Load users on mount
    useEffect(() => {
        fetchUsers()
    }, [])

    const fetchUsers = async () => {
        try {
            setLoading(true)
            const response = await api.get('/admin-users')
            const data = Array.isArray(response.data) ? response.data : response.data?.items || []
            setUsers(data)
        } catch (error) {
            toast.error('Erro ao carregar usuários')
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    // Filter and search users
    const filteredUsers = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    )

    // Handle form submission (create or edit)
    const handleFormSubmit = async (e) => {
        e.preventDefault()
        setFormErrors({})

        // Validate inputs
        if (!formData.username.trim()) {
            setFormErrors({ username: 'Nome de usuário é obrigatório' })
            return
        }
        if (!formData.email.trim()) {
            setFormErrors({ email: 'Email é obrigatório' })
            return
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(formData.email)) {
            setFormErrors({ email: 'Email inválido' })
            return
        }

        try {
            setIsSubmitting(true)

            if (editingUser) {
                // Update existing user
                await api.put(`/admin-users/${editingUser.id}`, {
                    username: formData.username.trim(),
                    email: formData.email.trim()
                })
                toast.success('Usuário atualizado com sucesso!')
            } else {
                // Create new user
                await api.post('/admin-users', {
                    username: formData.username.trim(),
                    email: formData.email.trim()
                })
                toast.success('Usuário criado com sucesso! Email de convite enviado.')
            }

            setFormData({ username: '', email: '' })
            setEditingUser(null)
            setIsModalOpen(false)
            fetchUsers()
        } catch (error) {
            const errorMsg = error.response?.data?.detail || (editingUser ? 'Erro ao atualizar usuário' : 'Erro ao criar usuário')
            if (error.response?.status === 409) {
                if (errorMsg.includes('nome de usuário')) {
                    setFormErrors({ username: errorMsg })
                } else if (errorMsg.includes('email')) {
                    setFormErrors({ email: errorMsg })
                } else {
                    toast.error(errorMsg)
                }
            } else {
                toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erro ao salvar usuário')
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    // Handle delete user
    const handleDeleteUser = async (userId) => {
        try {
            await api.delete(`/admin-users/${userId}`)
            toast.success('Usuário removido com sucesso')
            setIsConfirmModalOpen(false)
            setConfirmAction(null)
            fetchUsers()
        } catch (error) {
            toast.error('Erro ao remover usuário')
            console.error(error)
        }
    }

    // Handle toggle block user
    const handleToggleBlock = async (userId) => {
        try {
            await api.patch(`/admin-users/${userId}/block`)
            toast.success('Status do usuário atualizado')
            fetchUsers()
        } catch (error) {
            toast.error('Erro ao atualizar usuário')
            console.error(error)
        }
    }

    // Handle resend invite
    const handleResendInvite = async (userId) => {
        try {
            await api.post(`/admin-users/${userId}/resend-invite`)
            toast.success('Email de convite reenviado com sucesso')
            setIsConfirmModalOpen(false)
            setConfirmAction(null)
        } catch (error) {
            toast.error('Erro ao reenviar email')
            console.error(error)
        }
    }

    const openEditModal = (user) => {
        setEditingUser(user)
        setFormData({ username: user.username, email: user.email })
        setFormErrors({})
        setIsModalOpen(true)
    }

    const openNewModal = () => {
        setEditingUser(null)
        setFormData({ username: '', email: '' })
        setFormErrors({})
        setIsModalOpen(true)
    }

    const openDeleteModal = (user) => {
        setSelectedUser(user)
        setConfirmAction('delete')
        setIsConfirmModalOpen(true)
    }

    const openResendModal = (user) => {
        setSelectedUser(user)
        setConfirmAction('resend')
        setIsConfirmModalOpen(true)
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        })
    }

    const plusIcon = <span className="material-icons sm">add</span>
    const editIcon = <span className="material-icons sm">edit</span>

    return (
        <div className="flex-1 overflow-auto">
            <div className="p-8">
                {/* Header with Title and Button */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <span className="material-icons text-blue-500 dark:text-blue-400" style={{fontSize: '40px'}}>admin_panel_settings</span>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Usuários Administrativos</h1>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Gerencie os usuários com acesso à plataforma</p>
                        </div>
                    </div>
                    <Button onClick={openNewModal} variant="primary">
                        {plusIcon} Novo Usuário
                    </Button>
                </div>

                {/* Search Bar */}
                <div className="mb-6">
                    <div className="relative">
                        <span className="material-icons absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" style={{fontSize: '20px'}}>search</span>
                        <input
                            type="text"
                            placeholder="Pesquisar por nome ou email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Users Table */}
                {loading ? (
                    <div className="flex justify-center p-12">
                        <span className="material-icons animate-spin text-blue-500" style={{ fontSize: '32px' }}>sync</span>
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="text-center p-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <span className="material-icons text-gray-400 mb-4" style={{ fontSize: '48px' }}>person_outline</span>
                        <p className="text-gray-600 dark:text-gray-400">
                            {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado ainda'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Usuário</th>
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Email</th>
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Status</th>
                                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Criado em</th>
                                    <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-white">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => (
                                    <tr
                                        key={user.id}
                                        className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
                                    >
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-medium">
                                            {user.username}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {user.email}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <span
                                                className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                                                    user.is_active
                                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                                }`}
                                            >
                                                {user.is_active ? 'Ativo' : 'Bloqueado'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {formatDate(user.created_at)}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <div className="flex justify-end gap-1">
                                                <button
                                                    onClick={() => openEditModal(user)}
                                                    title="Editar usuário"
                                                    className="p-2 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20 transition"
                                                >
                                                    <span className="material-icons" style={{ fontSize: '18px' }}>edit</span>
                                                </button>
                                                <button
                                                    onClick={() => handleToggleBlock(user.id)}
                                                    title={user.is_active ? 'Bloquear usuário' : 'Desbloquear usuário'}
                                                    className={`p-2 rounded-lg transition ${
                                                        user.is_active
                                                            ? 'text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20'
                                                            : 'text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/20'
                                                    }`}
                                                >
                                                    <span className="material-icons" style={{ fontSize: '18px' }}>
                                                        {user.is_active ? 'block' : 'check_circle'}
                                                    </span>
                                                </button>
                                                <button
                                                    onClick={() => openResendModal(user)}
                                                    title="Reenviar email de convite"
                                                    className="p-2 rounded-lg text-cyan-600 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/20 transition"
                                                >
                                                    <span className="material-icons" style={{ fontSize: '18px' }}>mail</span>
                                                </button>
                                                <button
                                                    onClick={() => openDeleteModal(user)}
                                                    title="Remover usuário"
                                                    className="p-2 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 transition"
                                                >
                                                    <span className="material-icons" style={{ fontSize: '18px' }}>delete</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Create/Edit User Modal */}
                <Modal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false)
                        setEditingUser(null)
                        setFormData({ username: '', email: '' })
                        setFormErrors({})
                    }}
                    title={editingUser ? 'Editar Usuário' : 'Novo Usuário'}
                >
                    <form onSubmit={handleFormSubmit} className="space-y-4">
                        <Input
                            label="Nome de Usuário"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            placeholder="Ex: joao.silva"
                            error={formErrors.username}
                            disabled={isSubmitting}
                        />
                        <Input
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="Ex: joao@empresa.com"
                            error={formErrors.email}
                            disabled={isSubmitting}
                        />

                        <div className="flex gap-3 pt-6">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                    setIsModalOpen(false)
                                    setEditingUser(null)
                                    setFormData({ username: '', email: '' })
                                    setFormErrors({})
                                }}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (editingUser ? 'Atualizando...' : 'Criando...') : (editingUser ? 'Atualizar' : 'Criar Usuário')}
                            </Button>
                        </div>
                    </form>
                </Modal>

                {/* Confirm Modal */}
                <Modal
                    isOpen={isConfirmModalOpen}
                    onClose={() => {
                        setIsConfirmModalOpen(false)
                        setConfirmAction(null)
                        setSelectedUser(null)
                    }}
                    title={confirmAction === 'delete' ? 'Remover Usuário' : 'Reenviar Convite'}
                >
                    {confirmAction === 'delete' && selectedUser && (
                        <div className="space-y-4">
                            <p className="text-gray-700 dark:text-gray-300">
                                Tem certeza que deseja remover o usuário <strong>{selectedUser.username}</strong>?
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Esta ação é irreversível.
                            </p>
                            <div className="flex gap-3 pt-6">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        setIsConfirmModalOpen(false)
                                        setConfirmAction(null)
                                        setSelectedUser(null)
                                    }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={() => handleDeleteUser(selectedUser.id)}
                                >
                                    Remover
                                </Button>
                            </div>
                        </div>
                    )}

                    {confirmAction === 'resend' && selectedUser && (
                        <div className="space-y-4">
                            <p className="text-gray-700 dark:text-gray-300">
                                Reenviar email de convite para <strong>{selectedUser.username}</strong>?
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Um novo link será gerado e enviado para {selectedUser.email}.
                            </p>
                            <div className="flex gap-3 pt-6">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        setIsConfirmModalOpen(false)
                                        setConfirmAction(null)
                                        setSelectedUser(null)
                                    }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={() => handleResendInvite(selectedUser.id)}
                                >
                                    Reenviar Email
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal>
            </div>
        </div>
    )
}

export default AdminUsersPage
